import { makeAutoObservable } from 'mobx'
import { gamificationStore, type GameId } from './gamificationStore'
import {
  openWaterCourse,
  getAllObjectives,
  getObjectiveById,
  getModuleById,
  type Course,
  type CourseObjective,
  type CourseModule,
} from '../courses/openWater'

export interface ObjectiveProgress {
  objectiveId: string
  completed: boolean
  completedAt?: number
  score?: number
  correctAnswers?: number
}

export interface ModuleProgress {
  moduleId: string
  objectivesCompleted: ObjectiveProgress[]
  unlocked: boolean
}

export interface CourseProgress {
  courseId: string
  modules: ModuleProgress[]
  currentModuleId: string | null
  /** Territorios desbloqueados en el mapa (por moduleId) */
  conqueredTerritories: string[]
}

const STORAGE_KEY = 'planetmoji_mojiland'

function loadStored(): Record<string, CourseProgress> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const data = JSON.parse(raw)
      return data.courses && typeof data.courses === 'object' ? data.courses : {}
    }
  } catch (e) {
    console.warn('Failed to load mojiland from storage')
  }
  return {}
}

function save(courses: Record<string, CourseProgress>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ courses }))
  } catch (e) {
    console.warn('Failed to save mojiland')
  }
}

function initCourseProgress(course: Course): CourseProgress {
  const modules: ModuleProgress[] = course.modules.map((m, i) => ({
    moduleId: m.id,
    objectivesCompleted: [],
    unlocked: i === 0,
  }))
  return {
    courseId: course.id,
    modules,
    currentModuleId: course.modules[0]?.id ?? null,
    conqueredTerritories: [],
  }
}

class MojilandStore {
  courses: Record<string, CourseProgress> = {}

  constructor() {
    const stored = loadStored()
    this.courses = stored
    this.ensureCourse('open-water')
    makeAutoObservable(this)
  }

  ensureCourse(courseId: string) {
    if (this.courses[courseId]) return
    this.courses[courseId] = initCourseProgress(openWaterCourse)
    save(this.courses)
  }

  getCourseProgress(courseId: string): CourseProgress | undefined {
    return this.courses[courseId]
  }

  getOpenWaterProgress(): CourseProgress {
    this.ensureCourse('open-water')
    return this.courses['open-water']!
  }

  getModuleProgress(courseId: string, moduleId: string): ModuleProgress | undefined {
    return this.courses[courseId]?.modules.find(m => m.moduleId === moduleId)
  }

  isObjectiveCompleted(courseId: string, objectiveId: string): boolean {
    const prog = this.courses[courseId]
    if (!prog) return false
    for (const m of prog.modules) {
      const obj = m.objectivesCompleted.find(o => o.objectiveId === objectiveId)
      if (obj?.completed) return true
    }
    return false
  }

  isModuleUnlocked(courseId: string, moduleId: string): boolean {
    const prog = this.courses[courseId]
    if (!prog) return false
    const mod = prog.modules.find(m => m.moduleId === moduleId)
    if (!mod) return false
    return mod.unlocked
  }

  isModuleCompleted(courseId: string, moduleId: string): boolean {
    const mod = getModuleById(moduleId)
    if (!mod) return false
    const modProg = this.getModuleProgress(courseId, moduleId)
    if (!modProg) return false
    return modProg.objectivesCompleted.filter(o => o.completed).length >= mod.objectives.length
  }

  /** Desbloquea el siguiente módulo si el actual está completo */
  completeObjective(
    courseId: string,
    objectiveId: string,
    score?: number,
    correctAnswers?: number
  ): void {
    this.ensureCourse(courseId)
    const prog = this.courses[courseId]!
    const objective = getObjectiveById(objectiveId)
    if (!objective) return

    const module = openWaterCourse.modules.find(m => m.objectives.some(o => o.id === objectiveId))

    if (!module) return

    const modProg = prog.modules.find(m => m.moduleId === module.id)
    if (!modProg) return

    const alreadyCompleted = modProg.objectivesCompleted.some(
      o => o.objectiveId === objectiveId && o.completed
    )
    if (alreadyCompleted) return

    modProg.objectivesCompleted.push({
      objectiveId,
      completed: true,
      completedAt: Date.now(),
      score,
      correctAnswers,
    })

    gamificationStore.addXP(objective.xpReward)

    if (this.isModuleCompleted(courseId, module.id)) {
      prog.conqueredTerritories.push(module.id)
      const nextModule = openWaterCourse.modules.find(m => m.requiresModuleId === module.id)
      if (nextModule) {
        const nextModProg = prog.modules.find(m => m.moduleId === nextModule.id)
        if (nextModProg) nextModProg.unlocked = true
      }
    }

    save(this.courses)
  }

  /** Verifica si el resultado de un juego completa un objetivo */
  tryCompleteFromGame(
    gameId: GameId,
    score: number,
    levelReached: number,
    won: boolean
  ): { completed: boolean; objectiveId?: string } {
    const objectives = getAllObjectives().filter(
      o => o.type === 'game' && o.gameId === gameId
    )
    for (const obj of objectives) {
      if (this.isObjectiveCompleted('open-water', obj.id)) continue
      const scoreOk = !obj.minScore || score >= obj.minScore
      const levelOk = obj.gameLevel === undefined || levelReached >= obj.gameLevel
      if (scoreOk && levelOk) {
        this.completeObjective('open-water', obj.id, score)
        return { completed: true, objectiveId: obj.id }
      }
    }
    return { completed: false }
  }

  /** Verifica si el resultado de un quiz completa un objetivo */
  tryCompleteFromQuiz(
    moduleId: string,
    correctAnswers: number,
    totalQuestions: number
  ): { completed: boolean; objectiveId?: string } {
    const objectives = getAllObjectives().filter(
      o => o.type === 'quiz' && o.quizModuleId === moduleId
    )
    for (const obj of objectives) {
      if (this.isObjectiveCompleted('open-water', obj.id)) continue
      const minOk = (obj.minCorrectAnswers ?? 0) <= correctAnswers
      if (minOk) {
        this.completeObjective('open-water', obj.id, undefined, correctAnswers)
        return { completed: true, objectiveId: obj.id }
      }
    }
    return { completed: false }
  }

  /** Progreso global del curso (0-1) */
  getCourseProgressRatio(courseId: string): number {
    const total = getAllObjectives().length
    if (total === 0) return 0
    const prog = this.courses[courseId]
    if (!prog) return 0
    let completed = 0
    for (const m of prog.modules) {
      completed += m.objectivesCompleted.filter(o => o.completed).length
    }
    return completed / total
  }

  /** Objetivos completados en el módulo */
  getCompletedCountInModule(courseId: string, moduleId: string): number {
    const modProg = this.getModuleProgress(courseId, moduleId)
    return modProg?.objectivesCompleted.filter(o => o.completed).length ?? 0
  }
}

export const mojilandStore = new MojilandStore()
